import type { DestinationContext, TrackingDestination } from '../types'

declare global {
  interface Window {
    fbq?: (...args: any[]) => void
    _fbq?: (...args: any[]) => void
  }
}

function ensureFacebookPixelBase() {
  if (typeof window === 'undefined') return
  if (typeof document === 'undefined') return
  if (typeof window.fbq === 'function') return

  // Meta Pixel bootstrap (lint-friendly version of the standard snippet)
  const fbq = function (...args: any[]) {
    const anyFbq = fbq as any
    if (typeof anyFbq.callMethod === 'function') {
      anyFbq.callMethod(...args)
      return
    }
    anyFbq.queue.push(args)
  } as any

  fbq.push = fbq
  fbq.loaded = true
  fbq.version = '2.0'
  fbq.queue = []

  window.fbq = fbq
  if (!window._fbq) window._fbq = fbq

  const src = 'https://connect.facebook.net/en_US/fbevents.js'
  const existing = document.querySelector(`script[src="${src}"]`)
  if (existing) return

  const script = document.createElement('script')
  script.async = true
  script.src = src
  const first = document.getElementsByTagName('script')[0]
  if (first?.parentNode) {
    first.parentNode.insertBefore(script, first)
  } else {
    document.head.appendChild(script)
  }
}

function safeNumber(value: unknown): number | undefined {
  if (typeof value === 'number' && Number.isFinite(value)) return value
  const n = typeof value === 'string' ? Number(value) : NaN
  return Number.isFinite(n) ? n : undefined
}

export class FacebookPixelDestination implements TrackingDestination {
  public readonly key = 'facebookPixel' as const
  public readonly requiredConsent = 'marketing' as const

  private pixelId: string | null = null
  private initialized = false

  init(ctx: DestinationContext) {
    const id = process.env.NEXT_PUBLIC_FACEBOOK_PIXEL_ID || ''
    this.pixelId = id.trim() ? id.trim() : null
    if (!this.pixelId) return

    // Only load when consent allows (marketing).
    if (!ctx.consent.marketing) return

    ensureFacebookPixelBase()
    if (typeof window === 'undefined' || typeof window.fbq !== 'function') return

    if (!this.initialized) {
      this.initialized = true
      window.fbq('init', this.pixelId)
    }

    // If we already have an identity, try to attach it (advanced matching).
    const email = ctx.identity?.email ? String(ctx.identity.email) : ''
    if (email.trim()) {
      try {
        window.fbq('init', this.pixelId, { em: email.trim().toLowerCase() })
      } catch {
        // ignore
      }
    }
  }

  identify(userId: string) {
    if (!this.pixelId) return
    if (typeof window === 'undefined' || typeof window.fbq !== 'function') return

    // Best-effort advanced matching (Pixel hashes on their side).
    try {
      window.fbq('init', this.pixelId, { em: String(userId).trim().toLowerCase() })
    } catch {
      // ignore
    }
  }

  track(
    eventName: string,
    properties?: Record<string, any>,
    _ctx?: DestinationContext,
    eventId?: string
  ) {
    if (!this.pixelId) return
    if (typeof window === 'undefined' || typeof window.fbq !== 'function') return

    const p = properties || {}

    // Keep payload conservative + aligned with Meta "standard events" where possible.
    if (eventName === 'Purchase') {
      const value = safeNumber(p.value)
      const currency = typeof p.currency === 'string' ? p.currency : undefined
      const numItems = safeNumber(p.num_items)

      const params: Record<string, any> = {}
      if (value != null) params.value = value
      if (currency) params.currency = currency
      if (numItems != null) params.num_items = numItems

      window.fbq('track', 'Purchase', params, eventId ? { eventID: eventId } : undefined)
      return
    }

    // Fallback: send custom event as-is (properties passed through).
    window.fbq('trackCustom', eventName, p, eventId ? { eventID: eventId } : undefined)
  }

  page() {
    if (!this.pixelId) return
    if (typeof window === 'undefined' || typeof window.fbq !== 'function') return
    window.fbq('track', 'PageView')
  }
}

